import { ScaleTime, Selection, EnterElement, ScaleBand, ScaleLinear } from "d3"
import { EEGMontageReactCallbacks, SdDetectionReactCallbacks } from "../../../../Types/ReactCallbacks"
import { EEGPage } from "../../../../Data/EEGPage"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { CompositeTimeSeriesData, TimeSeriesData } from "../../../../Data/TimeSeriesData"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { getColors, MobergTheme } from "../../../../../../../../Moberg"
import { LABEL_SETTINGS } from "../../../../../../../../Constants/LabelSettings"
import { PageRectangle } from "../../../../Types/Trace"
import { drawEEGTrace } from "./drawEEGTrace"

export type EEGCanvasConfig = {
	graphId: string
	viewScale: ScaleTime<any, any, any>
	channelScale: ScaleBand<string>
	sensitivityScaleVoltsToPixels: ScaleLinear<any, any, any>
	ecgSensitivityScaleVoltsToPixels?: ScaleLinear<any, any, any>
	sensitivityMicroVolts: number
	eegModality: string | undefined
	dataObjectId: number
	color?: string
	ecgColor?: string
	ecgChannelKeys?: Set<string>
	ecgSensitivity?: number
	calibrationToken?: string
	hiddenChannelKeys: Set<string>
	spacerChannelKeys?: Set<string>
}

export class D3EEGCanvas extends D3OneToOneRenderable<SVGGElement, SVGForeignObjectElement, EEGCanvasConfig, EEGMontageReactCallbacks | SdDetectionReactCallbacks> {
	private context?: CanvasRenderingContext2D | null
	private pageManager: TimeSeriesPageManager<EEGPage>
	private offscreenCanvas: OffscreenCanvas
	private offscreenCanvasContext: OffscreenCanvasRenderingContext2D | null
	private pageRectangle = { x: 0, y: 0, width: 0, height: 0 }
	private get placeholderColor() { return getComputedStyle(document.documentElement).getPropertyValue("--color-chart-placeholder").trim() || getColors(MobergTheme.GRAY).main }
	private colorSchemeObserver: MutationObserver

	constructor(root: SVGGElement, config: EEGCanvasConfig, pageManager: TimeSeriesPageManager<EEGPage>, reactCallbacks: EEGMontageReactCallbacks | SdDetectionReactCallbacks) {
		super(root, config, "d3-eeg-canvas", reactCallbacks)
		this.pageManager = pageManager

		const width = config.viewScale.range()[1]
		const height = config.channelScale.range()[1]
		this.offscreenCanvas = new OffscreenCanvas(width, height)
		this.offscreenCanvasContext = this.offscreenCanvas.getContext("2d")

		this.colorSchemeObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.attributeName === "data-color-scheme") {
					this.render()
					break
				}
			}
		})
		this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

		this.render()
	}

	public destroy() {
		this.colorSchemeObserver.disconnect()
	}

	public renderPage = (page: EEGPage | undefined) => {
		if (!page || !this.context || !this.config.eegModality || !this.offscreenCanvasContext) {
			return
		}

		const { x, y, width, height } = this.getPageRectangle(page.startTime, page.endTime)
		this.context.clearRect(x, y, width, height)

		// Check if there is a cached render at the same resolution
		const cachedRender = page.renderCache.get(this.config.dataObjectId)?.get(this.getRenderCacheKey())

		if (cachedRender && !cachedRender.dirty) {
			const bitmap = cachedRender.bitmap

			if (this.context.canvas.width === bitmap.width && this.context.canvas.height === bitmap.height) {
				this.context.drawImage(bitmap, x, y)
				return
			}
		}

		const pageData = page.data.get(this.config.dataObjectId)
		const montageData = pageData?.get(this.config.eegModality) as CompositeTimeSeriesData | undefined

		// If no data exists (page still loading), show gray.
		if (!montageData) {
			this.context.fillStyle = this.placeholderColor
			this.context.fillRect(x, y, width, height)
			return
		}

		// If the data came back empty, we can't draw anything on the screen, but we want to show that the page loaded.
		if (montageData && (montageData.data.length === 0 || montageData.times.length === 0)) {
			return
		}

		this.offscreenCanvasContext?.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height)

		const spacerKeys = this.config.spacerChannelKeys ?? new Set<string>()
		const ecgKeys = this.config.ecgChannelKeys ?? new Set<string>()

		let eegDataIndex = 0

		for (const channelPair of this.config.channelScale.domain()) {
			if (spacerKeys.has(channelPair)) {
				continue
			}

			const isECG = ecgKeys.has(channelPair)
			const ecgData = isECG
				? pageData?.get(channelPair) as TimeSeriesData | undefined
				: undefined
			const channelData = isECG
				? ecgData?.data
				: montageData.data[eegDataIndex]
			const channelTimes = isECG
				? ecgData?.times
				: montageData.times

			if (!isECG) {
				eegDataIndex += 1
			}

			if (!channelData || !channelTimes) {
				continue
			}

			if (this.context && !this.config.hiddenChannelKeys.has(channelPair)) {
				const center = (this.config.channelScale(channelPair) as number) + this.config.channelScale.step() / 2
				const baseSensitivityScale = (
					isECG
					? this.config.ecgSensitivityScaleVoltsToPixels
					: this.config.sensitivityScaleVoltsToPixels
				) ?? this.config.sensitivityScaleVoltsToPixels
				const sensitivityScale = isECG
					? baseSensitivityScale.copy().domain(baseSensitivityScale.domain().map(value => -value))
					: baseSensitivityScale

				const newRange = [sensitivityScale.range()[0] + center, sensitivityScale.range()[1] + center]
				const newYScale = sensitivityScale.copy().range(newRange)

				const color = isECG
					? (this.config.ecgColor ?? LABEL_SETTINGS["ECG_II"].color)
					: this.config.color ?? (getComputedStyle(document.documentElement).getPropertyValue("--color-chart-axis-text").trim() || "black")

				const pixelsPerMs = width / (page.endTime - page.startTime)
				drawEEGTrace(this.offscreenCanvasContext, channelTimes, channelData, page.startTime, pixelsPerMs, newYScale, color)
			}
		}

		// Draw the unloaded region, if loading in chunks
		this.drawUnloadedRegion(page)

		const bitmap = this.offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(this.config.dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges: [] })
		this.context.drawImage(bitmap, x, y)
	}

	private drawUnloadedRegion = (page: EEGPage) => {
		const rect = this.getUnloadedRectangle(page)

		if (!this.offscreenCanvasContext || !rect) {
			return
		}

		this.offscreenCanvasContext.fillStyle = this.placeholderColor
		this.offscreenCanvasContext.fillRect(rect.x, rect.y, rect.width, rect.height)
	}

	private getUnloadedRectangle = (page: EEGPage): PageRectangle | null => {
		if (!this.offscreenCanvasContext || !this.config.eegModality) {
			return null
		}

		const unloadedStartTime = page.responseBuffers.get(this.config.eegModality)?.unloadedStartTime

		if (unloadedStartTime === Infinity || unloadedStartTime === null || !unloadedStartTime) {
			return null
		}

		// The offscreen canvas uses the page start time as the origin, so, these x coordinates have to be relative to that 0 point.
		const x0 = this.config.viewScale(page.startTime)
		const x1 = this.config.viewScale(unloadedStartTime) - x0
		const x2 = this.config.viewScale(page.endTime) - x0

		return { x: x1, y: 0, width: x2-x1, height: this.offscreenCanvas.height }
	}

	private getRenderCacheKey = () => {
		const hiddenKeysToken = Array.from(this.config.hiddenChannelKeys).sort().join("|") || "visible"
		const colorScheme = document.documentElement.getAttribute("data-color-scheme") ?? "light"
		const domainToken = this.config.channelScale.domain().join(",")
		return `${this.config.graphId}-${domainToken}-${this.config.eegModality}-${this.config.sensitivityMicroVolts}-${this.config.ecgSensitivity ?? "none"}-${this.config.calibrationToken ?? "uncalibrated"}-${hiddenKeysToken}-${colorScheme}`
	}

	private getPageRectangle = (startTime: Date | number, endTime: Date | number) => {
		const x1 = this.config.viewScale(startTime)
		const x2 = this.config.viewScale(endTime)
		const width = x2 - x1
		const height = this.config.channelScale.range()[1]

		this.pageRectangle.x = Math.round(x1)
		this.pageRectangle.width = width
		this.pageRectangle.height = height

		return this.pageRectangle
	}

	protected updateDerivedState = () => {
		this.offscreenCanvas.width = this.config.viewScale.range()[1]
		this.offscreenCanvas.height = this.config.channelScale.range()[1]
	}

	protected enter = (newTrace: Selection<EnterElement, any, any, any>): Selection<SVGForeignObjectElement, any, any, any> => {
		const foreignObject = newTrace.append("foreignObject").attr("class", this.className)
		const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, any, any, any>

		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]
		const height = this.config.channelScale?.range()[1] - this.config.channelScale?.range()[0]

		foreignObject.attr("width", width).attr("height", height)
		canvas.attr("width", width).attr("height", height)

		const canvasNode = canvas.node()

		if (canvasNode != null) {
			this.context = canvasNode.getContext("2d") ?? undefined

			// Show a blank gray screen on the first render
			if (this.context) {
				this.context.fillStyle = this.placeholderColor
				this.context.fillRect(0, 0, width, height)
			}
		}

		this.pageManager.getPagesInView().forEach(page => this.renderPage(page))

		return foreignObject
	}

	protected update = (updatedForeignObject: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		const updatedCanvas = updatedForeignObject.select("canvas")

		const width = this.config.viewScale?.range()[1] - this.config.viewScale?.range()[0]
		const height = this.config.channelScale?.range()[1] - this.config.channelScale?.range()[0]

		updatedForeignObject.attr("width", width).attr("height", height)
		updatedCanvas.attr("width", width).attr("height", height)

		this.pageManager.getPagesInView().forEach(page => this.renderPage(page))

		return updatedForeignObject
	}
}
